import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
    SortOrder,
    listFiltersService,
    ListTableSort,
    DropdownOption,
    mapToDropdownOption,
    getEnumValues,
    depascalize
} from "sprint-common-web";

import { DistributorsDataContext } from "../../../../common/data/lookup-data-providers/DistributorsDataProvider";
import { OrderAirlinesDataContext } from "../../../common/data-providers/OrderAirlinesDataProvider";
import { OrderCompletionProgress } from "../../../common/types/OrderCompletionProgress";
import { OrderStatus } from "../../../common/types/OrderStatus";
import { getCompletionProgressOptions } from "../../../common/utils/getCompletionProgressOptions";
import { getUniqueSystemDropdownOptions } from "../../common/getUniqueSystemDropdownOptions";
import { getStatusName } from "../../common/utils/getStatusName";
import { OrderListingContext } from "../context/OrderListingContext";
import { CloudOrderSource } from "../OrderListingItem";

export interface OrderListingFilters {
    page: number;
    pageSize: number;
    sortBy: string;
    sortOrder: SortOrder;
    statuses: number[];
    airlineIds?: string[];
    distributorId?: string;
    distributorName?: string;
    systemIds?: string[];
    content?: string;
    pONumber?: string;
    filename?: string;
    progresses?: number[];
    sources?: number[];
    dueDateFrom?: Date;
    dueDateTo?: Date;
    startDateFrom?: Date;
    startDateTo?: Date;
    orderIds?: string[];
}

const statuses = [
    OrderStatus.Pending,
    OrderStatus.Approved,
    OrderStatus.EncodingFailed,
    OrderStatus.Encoded,
    OrderStatus.DeliveryApproved,
    OrderStatus.DeliveryFailed,
    OrderStatus.DeliveryCompleted,
    OrderStatus.Rejected,
    OrderStatus.DeliveryError,
    OrderStatus.Cancelled,
    OrderStatus.Overridden
];

const sources = getEnumValues(CloudOrderSource);

const defaultFilters: OrderListingFilters = {
    page: 1,
    pageSize: 50,
    sortBy: "createdDate",
    sortOrder: "desc",
    statuses,
    airlineIds: undefined,
    distributorId: undefined,
    distributorName: undefined,
    systemIds: undefined,
    content: "",
    pONumber: "",
    filename: "",
    progresses: getEnumValues(OrderCompletionProgress),
    sources,
    dueDateFrom: undefined,
    dueDateTo: undefined,
    startDateFrom: undefined,
    startDateTo: undefined
};

export const useOrderListingFilters = () => {
    const { airlines } = useContext(OrderAirlinesDataContext);
    const { listingConfig } = useContext(OrderListingContext);

    const history = useHistory();
    const location = useLocation();

    const { suggestDistributor } = useContext(DistributorsDataContext);

    const filtersService = useMemo(() => listFiltersService<OrderListingFilters>(defaultFilters), []);

    const [filters, setFilters] = useState<OrderListingFilters>(() => {
        const fetchedFilters = filtersService.getSavedFilters(location.search);

        return { ...fetchedFilters, pageSize: listingConfig.pageSize ?? fetchedFilters.pageSize };
    });

    useEffect(() => {
        filtersService.saveFilters(location.pathname, history, filters);
    }, [location.pathname, history, filters]);

    const setSort = (sort: ListTableSort) => {
        setFilters((prevFilters: OrderListingFilters) => ({
            ...prevFilters,
            sortBy: sort.by,
            sortOrder: sort.order,
            page: 1
        }));
    };

    const setPage = (page: number) => {
        setFilters((prevFilters: OrderListingFilters) => ({ ...prevFilters, page }));
    };

    const setPageSize = (pageSize: number): void => {
        setFilters((prevFilters: OrderListingFilters) => ({ ...prevFilters, pageSize, page: 1 }));
    };

    const setStatuses = (orderStatuses: number[]): void => {
        setFilters((prevFilters: OrderListingFilters) => ({ ...prevFilters, statuses: orderStatuses, page: 1 }));
    };

    const setSources = (orderSources: number[]): void => {
        setFilters((prevFilters: OrderListingFilters) => ({ ...prevFilters, sources: orderSources, page: 1 }));
    };

    const setDistributor = (distributorId?: string, distributorName?: string): void => {
        setFilters((prevFilters: OrderListingFilters) => ({ ...prevFilters, distributorId, distributorName, page: 1 }));
    };

    const setFilename = (filename: string): void => {
        setFilters((prevFilters: OrderListingFilters) => ({ ...prevFilters, filename, page: 1 }));
    };

    const setAirlines = (options: DropdownOption[]): void => {
        setFilters((prevFilters: OrderListingFilters) => ({
            ...prevFilters,
            airlineIds: options.map((option) => String(option.key)),
            systemIds: undefined,
            page: 1
        }));
    };

    const setSystems = (options: DropdownOption[]): void => {
        setFilters((prevFilters: OrderListingFilters) => ({
            ...prevFilters,
            systemIds: options.map((option) => option.key),
            page: 1
        }));
    };

    const setContent = (content?: string): void => {
        setFilters((prevFilters: OrderListingFilters) => ({ ...prevFilters, content, page: 1 }));
    };

    const setPONumber = (pONumber?: string): void => {
        setFilters((prevFilters: OrderListingFilters) => ({ ...prevFilters, pONumber, page: 1 }));
    };

    const setProgresses = (progresses?: number[]): void => {
        setFilters((prevFilters: OrderListingFilters) => ({ ...prevFilters, progresses, page: 1 }));
    };

    const setDueDateFrom = (dueDateFrom: Date) => {
        setFilters((prevFilters: OrderListingFilters) => ({ ...prevFilters, dueDateFrom }));
    };

    const setDueDateTo = (dueDateTo: Date) => {
        setFilters((prevFilters: OrderListingFilters) => ({ ...prevFilters, dueDateTo }));
    };

    const setStartDateFrom = (startDateFrom: Date) => {
        setFilters((prevFilters: OrderListingFilters) => ({ ...prevFilters, startDateFrom, page: 1 }));
    };

    const setStartDateTo = (startDateTo: Date) => {
        setFilters((prevFilters: OrderListingFilters) => ({ ...prevFilters, startDateTo, page: 1 }));
    };

    const clearFilters = (): void => {
        setFilters(defaultFilters);
    };

    const statusOptions = statuses.map((status) => ({ key: status, text: getStatusName(status) }));

    const sourceOptions = sources.map((source) => ({ key: source, text: depascalize(CloudOrderSource[source]) }));

    const airlineOptions = airlines.map((airline) => mapToDropdownOption(airline));

    const progressOptions = getCompletionProgressOptions();

    const systemOptions = getUniqueSystemDropdownOptions(airlines, filters.airlineIds);

    const filterDistributorOptions = async (query: string) => {
        const distributors = await suggestDistributor(query);

        return distributors.map((distributor) => mapToDropdownOption(distributor));
    };

    return {
        filters,
        statusOptions,
        airlineOptions,
        systemOptions,
        sourceOptions,
        progressOptions,
        filterDistributorOptions,
        setPage,
        setPageSize,
        setSort,
        setStatuses,
        setSources,
        setDistributor,
        setAirlines,
        setSystems,
        setPONumber,
        setContent,
        setFilename,
        setProgresses,
        setDueDateFrom,
        setDueDateTo,
        setStartDateFrom,
        setStartDateTo,
        clearFilters
    };
};
